import { connect, useDispatch } from 'react-redux';
import { Dispatch, useEffect, useMemo, useRef, useState } from 'react';
import { FieldArray } from 'react-final-form-arrays';
import { FormSpy, useField } from 'react-final-form';
import { v4 as uuidv4 } from 'uuid';

import loaderImage from 'assets/loader.gif';
import { StructureRequirementsNpaModal } from 'KnmPackage/StructureRequirementsNpaModule/StructureRequirementsNpaModal';
import { IRequirementNpa } from 'APIPackage/RequirementsNpaModule/interfaces/IRequirementNpa';
import { IRequirementStructuralUnit } from 'APIPackage/RequirementsNpaModule/interfaces/IRequirementStructuralUnit';
import { IRequirementsNpaInitValues } from 'KnmPackage/RequirementsNpaModalModule/components/RequirementsNpaModal';
import { IKnmPmFormReason } from 'KnmPmPackage/MainModule/interfaces/IKnmPmFormReason';
import { KnmReasonTypeNameEnum } from 'KnmPackage/MainModule/enums/KnmReasonTypeNameEnum';

import { IKnmFormRef } from '../../../KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { getKnmDataCreated, getKnmFormRef } from '../../../KnmPackage/MainModule/selectors/knmSelectors';
import { useLinkedPassportValidation } from '../../../KnmErknmPackage/MainModule/hooks/useLinkedPassportValidation';
import { Button } from '../../../UIPackage/MainModule/components/Button';
import styles from './PmRequirements.module.scss';
import { IDictionaryKnoOrganizationSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionaryKnoOrganizationSelectOption';
import { IDictionarySupervisionTypeSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySupervisionTypeSelectOption';
import { FieldBlock } from '../../../UIPackage/FormModule/components/FieldBlock';
import { REQUIREMENTS_NPA_BLOCK_ID } from '../../MainModule/constants/knmPmFieldsElementsIds';
import { IRequirementsNpaItem } from '../../../KnmPackage/RequirementsNpaModalModule/interfaces/IRequirementsNpaItem';
import { PmRequirementsItem } from './PmRequirementsItem';
import { getTitleFieldWithRedStar } from '../../../functions/getTitleFieldWithRedStar';
import { IKnmErknmFormObject } from '../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormObject';
import { IKnmFormRiskIndicators } from '../../../KnmPackage/MainModule/interfaces/IKnmFormRiskIndicators';
import { coreSetLoading } from '../../../CorePackage/MainModule/actions/coreActions';
import {
  chekMandatoryRequirement,
  fetchNpaFull,
  IChekMandatoryRequirementFilters,
  IFetchStructuresUnitsFilter,
  structuralUnitConverter,
} from '../../../APIPackage/RequirementsNpaModule/services/requirementsNpaApi';
import { addNoticeAction } from '../../../CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from '../../../CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from '../../../CorePackage/NotificationModule/enums/MessageTypesEnum';
import { RootActions } from '../../../CorePackage/MainModule/types/RootActions';
import { useDebouncedValue } from '../../../hooks/useDebouncedMemo';
import { useLatestValue } from '../../../hooks/useLatestValue';
import { IKnmPmFormRequirementsNpa } from '../../MainModule/interfaces/IKnmPmFormRequirementsNpa';
import { arraysAreEqual } from '../../../functions/arraysAreEqual';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { isLaterThanAdminSetting } from '../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { IRequirementsMandatory } from '../../../APIPackage/RequirementsNpaModule/interfaces/IRequirementsMandatory';

interface IPmRequirementsProps {
  isLinkedByImpossibility?: boolean;
  knoOrganization?: IDictionaryKnoOrganizationSelectOption;
  objectsPm?: IKnmErknmFormObject[];
  reasonFieldValue: IKnmPmFormReason | undefined;
  riskIndicators?: IKnmFormRiskIndicators[];
  supervisionType?: IDictionarySupervisionTypeSelectOption;
  watchOnly: boolean;
}

interface IPmRequirementsStoreProps {
  formRef: IKnmFormRef | undefined;
  knmCreationDate: string | null | undefined;
  settings: IAdminSetting[];
}

const PmRequirementsWithoutStore = ({
  isLinkedByImpossibility,
  knoOrganization,
  objectsPm,
  reasonFieldValue,
  riskIndicators,
  supervisionType,
  watchOnly,
  formRef,
  knmCreationDate,
  settings,
}: IPmRequirementsProps & IPmRequirementsStoreProps) => {
  const requirementsFieldName = 'requirementsNpa';
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const update = formRef?.current.mutators.update;
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [isRequirementsModalVisible, setIsRequirementsModalVisible] = useState(false);
  const [initValues, setInitValues] = useState<IRequirementsNpaInitValues | undefined>(undefined);
  const [visibleCount, setVisibleCount] = useState(20);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // Данные параметры используются для фильтра списка ОТ при их добавлении,
  // при изменении этих фильтров нам нужно убедиться, что уже выбранные ОТ все еще им соответствуют или выдать ошибку валидации
  const [currentRequirementsFilters, setCurrentRequirementsFilters] = useState<IChekMandatoryRequirementFilters>({
    objectTypeId: undefined,
    objectKindId: undefined,
    objectSubkindId: undefined,
    riskIndicatorId: undefined,
  });

  const isRequirementDeleted = useField<boolean>('isRequirementDeleted').input.value ?? false;

  const isLaterThanNpaValidationSetting: boolean =
    !!knmCreationDate &&
    isLaterThanAdminSetting(
      knmCreationDate,
      settings,
      AdminSettingCodesEnum.NPA_VALIDATION_ERVK_ROT_4_1_24_STARTS_FROM,
    );

  const isFirstLoad = useRef<boolean>(true);

  const isDisabled = watchOnly || !knoOrganization || !supervisionType;
  const isImpossibilityOfPrevious = reasonFieldValue?.type?.name === KnmReasonTypeNameEnum.IMPOSSIBILITY_OF_PREVIOUS;
  const isPrescriptionCheckReason = reasonFieldValue?.type?.name === KnmReasonTypeNameEnum.PRESCRIPTION_CHECK;
  const linkPassportId = reasonFieldValue?.links?.find(e => e.id)?.id;
  const isEnablePrescriptionCheck = isLaterThanAdminSetting(
    knmCreationDate,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_105_166_25_4_1_30_STARTS_FROM,
  );

  // Если паспорт имеет ссылку на исходный, то нужно проверить его основание для отображения валидации ФОТ + КНО + ВК
  const isNeedDisableNpaValidation = useLinkedPassportValidation({
    linkPassportId,
    settings,
    isPrescriptionCheckReason,
    isImpossibilityOfPrevious,
    isEnablePrescriptionCheck,
  });
  const isNeedDisableNpaValidationRef = useLatestValue(isNeedDisableNpaValidation);

  // @ts-ignore
  const requirementsNpaValues: IKnmPmFormRequirementsNpa[] | undefined = formRef?.current.getFieldState(
    requirementsFieldName,
  )?.value;

  const npaUsedIds = useMemo(() => {
    const arr = [] as number[];

    requirementsNpaValues?.forEach((el: IKnmPmFormRequirementsNpa) => {
      el.mandatory?.forEach((item: IRequirementsMandatory) => {
        if (item?.id) {
          arr.push(item.id);
        }
      });
    });

    return arr;
  }, [requirementsNpaValues]);

  const openRequirementsModal = () => {
    setInitValues(undefined);
    setIsRequirementsModalVisible(true);
  };

  const handleAddRequirementsNpa = (npaItems: IRequirementsNpaItem[]) => {
    const requirementsNpa = formRef?.current?.getState()?.values?.requirementsNpa ?? [];

    push &&
      update &&
      npaItems.forEach(npa => {
        const existedNpaWithSUIndex = requirementsNpa?.findIndex(
          item => item.npa.id === npa.npa.id && item.structuralUnit.id === npa.structuralUnit.id,
        );

        // Если есть НПА с нужной СЕ, добавляем формулировку в его список
        if (existedNpaWithSUIndex > -1) {
          update(requirementsFieldName, existedNpaWithSUIndex, {
            ...requirementsNpa[existedNpaWithSUIndex],
            mandatory: npa.mandatory ?? [],
          });
        } else {
          const existedNpaIndex = requirementsNpa?.findIndex(
            item =>
              item.npa.id === npa.npa.id &&
              item.structuralUnit.id !== npa.structuralUnit.id &&
              !item?.mandatory?.length,
          );
          const newNPA = { ...npa, isNew: true };

          // Если есть НПА, внутри формулировки отсутствуют и СЕ не соответствует СЕ из добавляемой формулировки - обновляем НПА (новый СЕ + формулировка)
          if (existedNpaIndex > -1) {
            update(requirementsFieldName, existedNpaIndex, newNPA);
          } else {
            // Иначе просто добавляем новый НПА
            push(requirementsFieldName, newNPA);
          }
        }
      });
  };

  const handleAddMandatory = (npa: IRequirementNpa, structuralUnit: IRequirementStructuralUnit) => {
    setInitValues({
      npa,
      structuralUnit,
    });
    setIsRequirementsModalVisible(true);
  };

  const handleShowMore = () => {
    setIsLoadingMore(true);
    setVisibleCount(prev => prev + 30);
    setIsLoadingMore(false);
  };

  const structuresUnitsFilters: IFetchStructuresUnitsFilter = useMemo(() => {
    const objectTypeIds = objectsPm
      ?.map(e => e.objectType?.value?.dictVersionId)
      ?.filter((e): e is string => typeof e === 'string');

    const objectKindIds = objectsPm
      ?.map(e => e.objectKind?.value?.dictVersionId)
      ?.filter((e): e is string => typeof e === 'string');

    const objectSubkindIds = objectsPm
      ?.map(e => e.objectSubKind?.value?.dictVersionId)
      ?.filter((e): e is string => typeof e === 'string');

    const riskIndicatorIds = riskIndicators
      ?.map(e => e.riskIndicator?.value?.dictVersionId)
      ?.filter((e): e is string => typeof e === 'string');

    return {
      objectTypeIds,
      objectKindIds,
      objectSubkindIds,
      riskIndicatorIds,
    };
  }, [objectsPm, riskIndicators]);

  // Оптимизация для серверной валидации выбранных ОТ после установки значений фильтров
  const structuresUnitsFiltersOptimize = useDebouncedValue(structuresUnitsFilters, 500);

  useEffect(() => {
    if (!isLaterThanNpaValidationSetting) return;
    // Ждём, пока определится флаг отключения валидации КНО/ВК, иначе валидация отработает по устаревшему значению
    if (isNeedDisableNpaValidation === undefined) return;

    const { objectSubkindId, objectKindId, objectTypeId, riskIndicatorId } = currentRequirementsFilters;
    const knoVersionId = knoOrganization?.value.dictVersionId || '';
    const supervisionVersionId = supervisionType?.value.dictVersionId || '';

    const requirements = formRef?.current.getState().values[requirementsFieldName] as
      | IKnmPmFormRequirementsNpa[]
      | undefined;

    const newFilters = { ...currentRequirementsFilters };
    let hasFilterChange = false;

    const objectSubkindIdFilter =
      Array.isArray(structuresUnitsFilters.objectSubkindIds) && structuresUnitsFilters.objectSubkindIds.length
        ? structuresUnitsFilters.objectSubkindIds[0]
        : undefined;

    if (objectSubkindId !== objectSubkindIdFilter) {
      newFilters.objectSubkindId = objectSubkindIdFilter;
      hasFilterChange = true;
    }

    const objectKindIdFilter =
      Array.isArray(structuresUnitsFilters.objectKindIds) && structuresUnitsFilters.objectKindIds.length
        ? structuresUnitsFilters.objectKindIds[0]
        : undefined;

    if (objectKindId !== objectKindIdFilter) {
      newFilters.objectKindId = objectKindIdFilter;
      hasFilterChange = true;
    }

    const objectTypeIdFilter =
      Array.isArray(structuresUnitsFilters.objectTypeIds) && structuresUnitsFilters.objectTypeIds.length
        ? structuresUnitsFilters.objectTypeIds[0]
        : undefined;

    if (objectTypeId !== objectTypeIdFilter) {
      newFilters.objectTypeId = objectTypeIdFilter;
      hasFilterChange = true;
    }

    const riskIndicatorIdFilter =
      Array.isArray(structuresUnitsFilters.riskIndicatorIds) && structuresUnitsFilters.riskIndicatorIds.length
        ? structuresUnitsFilters.riskIndicatorIds
        : undefined;

    if (!arraysAreEqual(riskIndicatorId, riskIndicatorIdFilter)) {
      hasFilterChange = true;
      newFilters.riskIndicatorId = riskIndicatorIdFilter;
    }

    setCurrentRequirementsFilters(newFilters);

    // Если какие-то из фильтров поменялись, то запускаем бек-енд валидацию соответствия добавленных ОТ измененным фильтрам
    if (hasFilterChange) {
      if (Array.isArray(requirements) && requirements.length > 0) {
        const npaRequest: IChekMandatoryRequirementFilters['npaRequest'] = [];

        requirements.forEach(requirement => {
          requirement?.mandatory?.forEach(item => npaRequest.push({ mrId: String(item.id) }));
        });
        try {
          chekMandatoryRequirement({
            supervisionVersionId,
            knoVersionId,
            ...newFilters,
            npaRequest,
          })
            .then(resp => {
              const validationMap = new Map(
                resp.mandatoryRequirements.map(i => [i.mandatoryRequirementId, i] as const),
              );
              let hasChanges = false;

              const nextRequirements = requirements.map(requirement => ({
                ...requirement,
                mandatory: requirement.mandatory.map(m => {
                  const validationResult = validationMap.get(m.id);
                  const hasInvalidControlObjectFilter =
                    validationResult?.objectKindIsCorrect === false ||
                    validationResult?.objectTypeIsCorrect === false ||
                    validationResult?.objectSubkindIsCorrect === false;
                  const hasInvalidRiscIndicatorsFilter = validationResult?.riskIndicatorIsCorrect === false;
                  const hasInvalidKno =
                    !isNeedDisableNpaValidationRef.current && validationResult?.knoVersionIsCorrect === false;
                  const hasInvalidSupervision =
                    !isNeedDisableNpaValidationRef.current && validationResult?.supervisionIsCorrect === false;

                  if (
                    m.hasInvalidControlObjectFilter !== hasInvalidControlObjectFilter ||
                    m.hasInvalidRiscIndicatorsFilter !== hasInvalidRiscIndicatorsFilter ||
                    m.hasInvalidKno !== hasInvalidKno ||
                    m.hasInvalidSupervision !== hasInvalidSupervision
                  ) {
                    hasChanges = true;
                  }

                  return {
                    ...m,
                    hasInvalidControlObjectFilter,
                    hasInvalidRiscIndicatorsFilter,
                    hasInvalidKno,
                    hasInvalidSupervision,
                  };
                }),
              }));

              if (hasChanges) {
                formRef?.current.change(requirementsFieldName, nextRequirements);
              }
            })
            .finally(() => {
              /**
               * После update полей hasInvalidControlObjectFilter и hasInvalidRiscIndicatorsFilter
               * в форме встречаются места, где происходит реакция на изменения (состояния dirty как всей формы, так и
               * непосредственно этих полей + сравнения в FormLeavingGuard). В finally, после загрузки паспорта и
               * инициализации этих полей сбрасываем initialState формы для исключения реакции на указанные выше поля.
               */
              if (isFirstLoad.current && formRef?.current) {
                const currentValues = formRef.current.getState().values;

                formRef.current.reset(currentValues);

                isFirstLoad.current = false;
              }
            });
        } catch (e) {
          console.error(e);
        }
      }
    }
  }, [structuresUnitsFiltersOptimize, isLaterThanNpaValidationSetting, isNeedDisableNpaValidation]);

  const addAllFields = async () => {
    if (!isDisabled) {
      dispatch(coreSetLoading(true));
      try {
        // Запрос списка всех ОТ с фильтрами
        const data = await fetchNpaFull({
          ...structuresUnitsFilters,
          knoVersionId: knoOrganization?.value.dictVersionId || '',
          supervisionVersionId: supervisionType?.value.dictVersionId || '',
        });

        const result: IRequirementsNpaItem[] = [];

        data.forEach(item => {
          item?.structuralUnits?.forEach(su => {
            const resultObj: IRequirementsNpaItem = {
              guid: uuidv4(),
              npa: item.npa,
              structuralUnit: structuralUnitConverter(su.structuralUnit),
              mandatory: su.mandatoryRequirements,
            };

            result.push(resultObj);
          });
        });

        formRef?.current.change('requirementsNpaErknm');
        handleAddRequirementsNpa(result);
        setIsRequirementsModalVisible(false);
      } catch (e) {
        dispatch(
          addNoticeAction({
            renderContent: 'Не удалось добавить все ОТ по индикатору',
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.INTERNAL_SERVER,
          }),
        );
      } finally {
        dispatch(coreSetLoading(false));
      }
    }
  };

  return (
    <>
      <FieldBlock
        label={getTitleFieldWithRedStar({
          label: 'Обязательные требования, подлежащие проверке',
          field: requirementsFieldName,
          isPm: true,
        })}
        id={REQUIREMENTS_NPA_BLOCK_ID}
        button={
          <>
            {!isLinkedByImpossibility && (
              <Button
                className="shared-btn-add"
                text="Добавить"
                type="button"
                variant="link"
                color="primary"
                id="pmRequirementsAddButton"
                disabled={isDisabled}
                onClick={openRequirementsModal}
              />
            )}
            {reasonFieldValue?.type?.indicatorRiskRequired && (
              <Button
                className="shared-btn-add"
                text="Добавить все ОТ по индикатору"
                type="button"
                variant="link"
                color="primary"
                id="erknmRequirementsNpaAddAllButton"
                disabled={isDisabled || !riskIndicators?.length}
                onClick={addAllFields}
              />
            )}
          </>
        }
      >
        <FormSpy subscription={{ errors: true, touched: true, values: true }}>
          {props => (
            <FieldArray name={requirementsFieldName}>
              {({ fields }) => {
                const totalItems = fields.value?.length || 0;
                const visibleItems = fields.value ? fields.value.slice(0, visibleCount) : [];

                const renderLoadMore = () => {
                  if (isLoadingMore) {
                    return (
                      <div style={{ textAlign: 'center', padding: '16px' }}>
                        <img src={loaderImage} alt="Загрузка..." style={{ width: '24px', height: '24px' }} />
                      </div>
                    );
                  }
                  if (totalItems > visibleCount) {
                    return (
                      <div className={styles.ShowMore}>
                        <Button
                          type="button"
                          variant="link"
                          color="primary"
                          text="Показать ещё"
                          onClick={handleShowMore}
                        />
                      </div>
                    );
                  }

                  return null;
                };

                return (
                  <>
                    {visibleItems.map((_, index) => {
                      const name = `${requirementsFieldName}[${index}]`;
                      const fieldValue = fields.value?.[index];

                      if (!fieldValue?.npa || !fieldValue.structuralUnit) return null;

                      return (
                        <PmRequirementsItem
                          key={name}
                          index={index}
                          name={name}
                          fieldValue={fieldValue}
                          isFieldTouched={props.touched?.requirementsNpa}
                          npaErrorField={props.errors?.requirementsNpa?.[index]}
                          watchOnly={watchOnly}
                          onAddMandatory={handleAddMandatory}
                          onRemoveRequirement={i => {
                            remove && remove('requirementsNpa', i);
                            formRef?.current?.change('isRequirementDeleted', true);
                          }}
                          onRemoveMandatory={(parentName, i) => remove && remove(`${parentName}.mandatory`, i)}
                          isLaterThanNpaValidationSetting={isLaterThanNpaValidationSetting}
                          isImpossibilityOfPrevious={isImpossibilityOfPrevious}
                        />
                      );
                    })}

                    {renderLoadMore()}
                  </>
                );
              }}
            </FieldArray>
          )}
        </FormSpy>
      </FieldBlock>

      {isRequirementsModalVisible && (
        <StructureRequirementsNpaModal
          onClose={() => setIsRequirementsModalVisible(false)}
          knoVersionId={knoOrganization?.value.dictVersionId || ''}
          supervisionVersionId={supervisionType?.value.dictVersionId || ''}
          objects={objectsPm}
          riskIndicators={riskIndicators}
          withRequirements={true}
          addRequirementsNpa={handleAddRequirementsNpa}
          initValues={initValues}
          ignoredNpaIds={npaUsedIds}
          usedNpa={requirementsNpaValues}
        />
      )}
      {isImpossibilityOfPrevious && isRequirementDeleted && (
        <div className={styles.Yellow}>
          Для добавления сведений в раздел необходимо добавить ссылку на паспорт мероприятия повторно
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state: IRootState): IPmRequirementsStoreProps => ({
  formRef: getKnmFormRef(state),
  settings: getAdminFetchSettings(state),
  knmCreationDate: getKnmDataCreated(state),
});

export const PmRequirements = connect(mapStateToProps)(PmRequirementsWithoutStore);
